import { useMemo } from 'react';
import { JobseekerCreateDto, JobseekerUpdateDto } from '@libs/dto';
import { jobseekerApi } from '../store/jobseeker/jobseeker.api';
import { useLoading, useSnackbar, useConfirmation } from '@carcareers/contexts';
import { isError, getErrorsAsString } from '@carcareers/utils';

interface JobseekerCallbacks {
  onSuccess?: () => void;
}

export const useJobseeker = (id?: number) => {
  const { setLoading, setLabel } = useLoading();
  const { toggleSnackbar } = useSnackbar();
  const { confirm } = useConfirmation();
  const [jobseekerCreate] = jobseekerApi.useJobseekerCreateMutation();
  const [jobseekerUpdate] = jobseekerApi.useJobseekerUpdateMutation();
  const [jobseekerDelete] = jobseekerApi.useJobseekerDeleteMutation();
  const [trigger] = jobseekerApi.endpoints.jobseekerIndex.useLazyQuery();
  const viewJobseeker = id !== undefined ? jobseekerApi.useJobseekerViewQuery(id) : undefined;

  return useMemo(
    () => ({
      trigger,
      viewJobseeker,
      createJobseeker: async (jobseeker: JobseekerCreateDto, callbacks?: JobseekerCallbacks) => {
        setLabel('Creating jobseeker...');
        setLoading(true);
        const response = await jobseekerCreate(jobseeker);

        if (isError(response)) {
          toggleSnackbar('error', getErrorsAsString(response));
        } else {
          toggleSnackbar('success', 'Jobseeker created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        }

        setLoading(false);
        return response;
      },
      updateJobseeker: async (jobseeker: JobseekerUpdateDto, callbacks?: JobseekerCallbacks) => {
        setLabel('Updating jobseeker...');
        setLoading(true);
        const response = await jobseekerUpdate(jobseeker);
        setLoading(false);
        
        if (!isError(response)) {
          toggleSnackbar('success', 'Jobseeker updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          toggleSnackbar('error', getErrorsAsString(response));
        }
        
        return response;
      },
      deleteJobseeker: async (id?: number, callbacks?: JobseekerCallbacks) => {
        if (!id) return;

        const confirmed = await confirm({
          title: 'Delete Jobseeker',
          message: 'Are you sure you want to delete this jobseeker?',
        });

        if (!confirmed) return;

        setLabel('Deleting jobseeker...');
        setLoading(true);
        const response = await jobseekerDelete(id);
        setLoading(false);
        
        if (!isError(response)) {
          toggleSnackbar('success', 'Jobseeker deleted');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          toggleSnackbar('error', getErrorsAsString(response));
        }
        
        return response;
      },
    }),
    [
      setLoading,
      toggleSnackbar,
      confirm,
      setLabel,
      jobseekerCreate,
      jobseekerUpdate,
      jobseekerDelete,
      trigger,
      viewJobseeker,
    ],
  );
};
